import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { RefreshCw, RotateCcw, AlertTriangle, CheckCircle, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatusBadge({ mtInStock, netOwed }) {
  if (netOwed === 0) return <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-green-100 text-green-700">CLEAR</span>;
  if (mtInStock >= netOwed) return <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-700">READY</span>;
  if (mtInStock > 0) return <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-700">PARTIAL</span>;
  return <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-red-100 text-red-700">PENDING</span>;
}

export function MTPending() {
  const { authHeader } = useAuth();
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/mt-tracking`, { headers: authHeader() })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const totalOwed    = data.reduce((s, r) => s + r.net_owed_to_vendor, 0);
  const totalInStock = data.reduce((s, r) => s + r.mt_in_stock,        0);
  const readyToSend  = data.filter(r => r.mt_in_stock >= r.net_owed_to_vendor && r.net_owed_to_vendor > 0).length;
  const urgentItems  = data.filter(r => r.mt_in_stock < r.net_owed_to_vendor && r.net_owed_to_vendor > 0).length;

  return (
    <div className="px-6 py-6">
      <div className="flex items-start justify-between mb-5 gap-4 flex-wrap">
        <div>
          <div className="small-label text-gray-400 mb-1 flex items-center gap-1.5">
            <RotateCcw size={11} /> MT Tracking
          </div>
          <h1 className="font-black text-3xl tracking-tight">MT Cylinder Tracking</h1>
          <p className="text-sm text-gray-500 mt-1">Empty (MT) cylinders owed to vendors — track what to return</p>
        </div>
        <button onClick={load} className="btn-secondary flex items-center gap-1.5 text-sm self-start" disabled={loading}>
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="small-label text-gray-400 mb-2">Total MT in Stock</div>
          <div className="font-black text-2xl text-gray-900">{totalInStock}</div>
          <div className="text-xs text-gray-400 mt-1">Across all godowns</div>
        </div>
        <div className={`border rounded-lg p-4 ${totalOwed > totalInStock ? 'border-red-200 bg-red-50' : 'bg-white border-gray-200'}`}>
          <div className="small-label text-gray-400 mb-2">Total Owed to Vendors</div>
          <div className={`font-black text-2xl ${totalOwed > totalInStock ? 'text-red-700' : 'text-gray-900'}`}>{totalOwed}</div>
          <div className="text-xs text-gray-400 mt-1">Net unreturned empties</div>
        </div>
        <div className="border border-green-200 bg-green-50 rounded-lg p-4">
          <div className="flex items-center gap-1.5 mb-2">
            <CheckCircle size={12} className="text-green-600" />
            <div className="small-label text-gray-400">Ready to Dispatch</div>
          </div>
          <div className="font-black text-2xl text-green-700">{readyToSend}</div>
          <div className="text-xs text-gray-400 mt-1">SKUs with enough stock</div>
        </div>
        <div className={`border rounded-lg p-4 ${urgentItems > 0 ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-white'}`}>
          <div className="flex items-center gap-1.5 mb-2">
            {urgentItems > 0 && <AlertTriangle size={12} className="text-amber-600" />}
            <div className="small-label text-gray-400">Need Follow-up</div>
          </div>
          <div className={`font-black text-2xl ${urgentItems > 0 ? 'text-amber-700' : 'text-gray-600'}`}>{urgentItems}</div>
          <div className="text-xs text-gray-400 mt-1">SKUs with shortage</div>
        </div>
      </div>

      {/* Alert if shortage */}
      {urgentItems > 0 && (
        <div className="mb-5 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-800 font-medium">
              {urgentItems} SKU{urgentItems > 1 ? 's' : ''} have fewer MT in stock than owed to vendor.
              Collect empties from customers or record returns.
            </p>
          </div>
          <Link to="/entry/adjustment" className="text-[12px] font-semibold text-amber-700 hover:text-amber-900 whitespace-nowrap flex items-center gap-1">
            Record MT Return <ArrowRight size={11} />
          </Link>
        </div>
      )}

      {/* Main table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {['Brand', 'Size', 'Total Purchased', 'Dispatched Back', 'Net Owed to Vendor', 'MT in Stock', 'Godown Breakdown', 'Status'].map(h => (
                  <th key={h} className="px-4 py-3 text-left small-label text-gray-500 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-10"><RefreshCw size={16} className="animate-spin text-gray-400 mx-auto" /></td></tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-gray-400 text-[13px]">
                    No purchase data yet. MT tracking starts once Gas Purchases are recorded.
                  </td>
                </tr>
              ) : data.map((r, i) => (
                <tr
                  key={i}
                  className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                    r.net_owed_to_vendor > r.mt_in_stock && r.net_owed_to_vendor > 0 ? 'bg-amber-50/30' : ''
                  }`}
                >
                  <td className="px-4 py-3 text-[13px] font-medium text-gray-900">{r.brand}</td>
                  <td className="px-4 py-3 text-[13px] text-gray-700">{r.size}</td>
                  <td className="px-4 py-3 text-[13px] text-gray-700">{r.total_purchased}</td>
                  <td className="px-4 py-3 text-[13px] text-green-700 font-medium">{r.dispatched_to_vendor || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`font-bold text-[14px] ${r.net_owed_to_vendor > 0 ? 'text-red-700' : 'text-gray-400'}`}>
                      {r.net_owed_to_vendor || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`font-semibold text-[14px] ${
                      r.mt_in_stock >= r.net_owed_to_vendor && r.net_owed_to_vendor > 0 ? 'text-green-700'
                      : r.mt_in_stock > 0 ? 'text-blue-700' : 'text-gray-400'
                    }`}>
                      {r.mt_in_stock || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {r.godown_breakdown.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {r.godown_breakdown.map((g, j) => (
                          <span key={j} className="text-[11px] bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">
                            {g.godown}: {g.mt}
                          </span>
                        ))}
                      </div>
                    ) : <span className="text-gray-400 text-[12px]">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge mtInStock={r.mt_in_stock} netOwed={r.net_owed_to_vendor} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2.5 border-t border-gray-100 text-xs text-gray-400 flex items-center justify-between">
          <span>{data.length} SKUs tracked</span>
          <Link to="/entry/adjustment" className="text-blue-600 hover:text-blue-800 text-xs font-medium flex items-center gap-0.5">
            Record MT Return / Dispatch <ArrowRight size={10} />
          </Link>
        </div>
      </div>

      {/* How it works */}
      <div className="mt-5 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="small-label text-gray-500 mb-2">How MT Tracking Works</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-[12px] text-gray-500">
          <div className="flex items-start gap-2">
            <span className="w-4 h-4 rounded-full bg-red-100 text-red-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">↑</span>
            <span><strong className="text-gray-700">Net Owed</strong> = Total cylinders purchased from vendor − empties dispatched back</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="w-4 h-4 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">□</span>
            <span><strong className="text-gray-700">MT in Stock</strong> = Empty cylinders currently in your godowns (via Sale MT field)</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="w-4 h-4 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">↓</span>
            <span><strong className="text-gray-700">To Return</strong> = Use Adjustment Entry → MT Dispatch to record when you send empties back</span>
          </div>
        </div>
      </div>
    </div>
  );
}
